// One (term × locale) cell of the S9 grid (Module 02 §4 S9).
//
// Shows the EFFECTIVE label plus a badge saying which layer produced it
// (`tenant` / `pack` / `base` — D-032), and, for a group_admin, turns into an
// inline editor. It owns only its own open/draft state; the mutations belong to
// the screen, which is what knows about query invalidation. That split is what
// lets this component be rendered in a test with no QueryClient and no network.
//
// Reset is offered ONLY when the tenant's own row is what is showing
// (`canReset`): DELETE removes the tenant row, so on a pack/base cell the button
// would do nothing while implying it would. The D-035 nuance — that resetting a
// tenant-edited PACK term lands on the base label, not the pack's — is stated in
// the hint, and the outcome the server reports is echoed back after the call.

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../components/status-chip";
import { WizardDialog } from "../../components/wizard-dialog";
import {
  LABEL_MAX,
  canReset,
  validateLabel,
  type TermCell as TermCellData,
  type TermLocaleCode,
} from "./terminology-model";

export type CellStatus = "saved" | "reset_base" | "reset_pack";

export interface TermCellProps {
  termKey: string;
  /** The term's display name, for the edit field's accessible label. */
  termName: string;
  locale: TermLocaleCode;
  cell: TermCellData | undefined;
  /** The four locale feeds load independently; an unloaded cell is honest. */
  isLoading: boolean;
  canEdit: boolean;
  busy: boolean;
  /** Outcome of the last mutation on THIS cell, echoed under it. */
  status: CellStatus | null;
  /** Which mutation failed on THIS cell, if any. */
  failed: "save" | "reset" | null;
  onSave: (label: string) => void;
  onReset: () => void;
}

export function TermCell({
  termKey,
  termName,
  locale,
  cell,
  isLoading,
  canEdit,
  busy,
  status,
  failed,
  onSave,
  onReset,
}: TermCellProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [confirmingReset, setConfirmingReset] = useState(false);

  const localeName = t(`onboarding.terminology.colLocale.${locale}`);

  if (isLoading && cell === undefined) {
    return <span className="tm-missing">{t("onboarding.terminology.cellLoading")}</span>;
  }
  if (cell === undefined) {
    return <span className="tm-missing">{t("onboarding.terminology.cellMissing")}</span>;
  }

  if (editing) {
    const problem = validateLabel(draft);
    return (
      <form
        className="tm-cell"
        onSubmit={(event) => {
          event.preventDefault();
          if (problem === null) {
            onSave(draft.trim());
            setEditing(false);
          }
        }}
      >
        <input
          className="ds-input tm-cell-input"
          aria-label={t("onboarding.terminology.editLabel", {
            term: termName,
            locale: localeName,
          })}
          value={draft}
          maxLength={LABEL_MAX}
          autoFocus
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        <p className="tm-cell-hint">
          {t("onboarding.terminology.charCount", { used: draft.trim().length, max: LABEL_MAX })}
        </p>
        {/* D-035: `terminology_overrides` stores ONE row per (key, locale), so a
            tenant edit overwrites the pack row in place and a later reset lands
            on BASE, not on the pack wording. Saying so at the moment of the edit
            is the difference between an informed change and a silent loss. */}
        {cell.source === "pack" ? (
          <p className="tm-cell-hint tm-cell-caution" role="note">
            {t("onboarding.terminology.overwritePackCaution")}
          </p>
        ) : null}
        {problem === "empty" ? (
          <p className="tm-cell-error">{t("onboarding.terminology.errorEmpty")}</p>
        ) : problem === "too_long" ? (
          <p className="tm-cell-error">
            {t("onboarding.terminology.errorTooLong", { max: LABEL_MAX })}
          </p>
        ) : null}
        <div className="tm-cell-actions">
          <button
            type="submit"
            className="ds-btn ds-btn-sm ds-btn-primary"
            disabled={problem !== null || busy}
          >
            {busy ? t("onboarding.terminology.saving") : t("onboarding.terminology.save")}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn-sm"
            onClick={() => {
              setEditing(false);
            }}
          >
            {t("onboarding.terminology.cancel")}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div className="tm-cell" data-source={cell.source} data-cell={`${termKey}:${locale}`}>
      <span className="tm-cell-head">
        <span className="tm-cell-label">{cell.label}</span>
        <span title={t(`onboarding.terminology.sourceTitle.${cell.source}`)}>
          <StatusChip
            variant={
              cell.source === "tenant" ? "ok" : cell.source === "pack" ? "accent" : "neutral"
            }
            dot={false}
          >
            {t(`onboarding.terminology.sourceBadge.${cell.source}`)}
          </StatusChip>
        </span>
      </span>

      {canEdit ? (
        <div className="tm-cell-actions">
          <button
            type="button"
            className="ds-btn ds-btn-sm"
            disabled={busy}
            onClick={() => {
              setDraft(cell.label);
              setEditing(true);
            }}
          >
            {t("onboarding.terminology.edit")}
          </button>
          {canReset(cell) ? (
            <button
              type="button"
              className="ds-btn ds-btn-sm"
              disabled={busy}
              onClick={() => {
                setConfirmingReset(true);
              }}
            >
              {busy ? t("onboarding.terminology.resetting") : t("onboarding.terminology.reset")}
            </button>
          ) : null}
        </div>
      ) : null}

      {/* Reset is not recoverable through the API (D-035), and every other
          destructive action in this module confirms first. This one now does
          too — and it names the term and locale it is about to revert. */}
      {confirmingReset ? (
        <WizardDialog
          testId="terminology-reset-confirm"
          destructive
          title={t("onboarding.terminology.resetConfirmTitle")}
          confirmLabel={t("onboarding.terminology.reset")}
          cancelLabel={t("onboarding.terminology.cancel")}
          busy={busy}
          onConfirm={() => {
            setConfirmingReset(false);
            onReset();
          }}
          onCancel={() => {
            setConfirmingReset(false);
          }}
        >
          <p>
            {t("onboarding.terminology.resetConfirmBody", {
              term: termName,
              locale: localeName,
              label: cell.label,
            })}
          </p>
        </WizardDialog>
      ) : null}

      {failed !== null ? (
        <p className="tm-cell-error" role="alert">
          {failed === "save"
            ? t("onboarding.terminology.saveFailed")
            : t("onboarding.terminology.resetFailed")}
        </p>
      ) : status !== null ? (
        <p className="tm-cell-status" role="status">
          {status === "saved"
            ? t("onboarding.terminology.savedTenant")
            : status === "reset_pack"
              ? t("onboarding.terminology.resetToPack")
              : t("onboarding.terminology.resetToBase")}
        </p>
      ) : null}
    </div>
  );
}
